import { Injectable, NgZone } from '@angular/core';
import { zendeskLog } from '../../zendesk-logger';
import { ChatAdapter } from './chat.adapter';

/**
 * Adapter do Zendesk Messenger (Web Widget for messaging).
 *
 * O launcher nativo fica oculto (por CSS) e o widget é controlado pelo nosso
 * FAB/toolbar — padrão de custom launcher do Zendesk (hide + open).
 */
@Injectable({
  providedIn: 'root',
})
export class MessengerChatAdapter implements ChatAdapter {
  // Só true depois que ZendeskMessengerComponent confirma sucesso do
  // `loginUser` (ver markReady). Sem essa guarda, um clique no FAB antes da
  // autenticação resolver chama `open()` sobre um widget ainda anônimo — a
  // mesma corrida que a inicialização do componente já evita para o `show`
  // automático, mas por este outro caminho (interação do usuário).
  private ready = false;

  constructor(private zone: NgZone) {}

  /** Função global injetada pelo snippet do Zendesk (carregamento assíncrono). */
  private get zE(): any {
    return (<any>window).zE;
  }

  /** Chamado por ZendeskMessengerComponent após o `loginUser` confirmar sucesso. */
  public markReady(): void {
    this.ready = true;
  }

  /** Chamado no logout/hide para não herdar o estado "ready" da sessão anterior. */
  public markNotReady(): void {
    this.ready = false;
  }

  /**
   * Abre o Messenger. No-op se o widget ainda não carregou ou se a
   * autenticação ainda não foi confirmada (ver `ready`) — nesse caso o clique
   * não deve abrir uma conversa anônima silenciosamente.
   */
  public show(): void {
    if (typeof this.zE === 'undefined') {
      return;
    }

    if (!this.ready) {
      zendeskLog('MessengerChatAdapter', 'Show ignorado: autenticação do Messenger ainda não confirmada.', 'warn');
      return;
    }

    // try/catch: se o widget carregado não expuser o namespace `messenger`,
    // logamos em vez de deixar o erro não tratado poluir o error tracking.
    // `show` antes de `open`: garante que o widget não está em estado `hide`
    // (nesse estado, `open` sozinho não renderiza o painel).
    this.zone.runOutsideAngular(() => {
      try {
        this.zE('messenger', 'show');
        this.zE('messenger', 'open');
      } catch (e) {
        zendeskLog('MessengerChatAdapter', `Não foi possível abrir o Messenger: ${e}`, 'error');
      }
    });
  }

  /**
   * Fecha o painel do Messenger (mantém a sessão; a conversa persiste no
   * servidor). Usado ao abrir o menu do FAB para o chat não ficar sobreposto.
   */
  public minimize(): void {
    if (typeof this.zE === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      try {
        this.zE('messenger', 'close');
      } catch (e) {
        zendeskLog('MessengerChatAdapter', `Não foi possível fechar o Messenger: ${e}`, 'error');
      }
    });
  }

  public tryRegisterUnreadListener(onCount: (count: number) => void): boolean {
    if (typeof this.zE === 'undefined') {
      return false;
    }

    this.zE('messenger:on', 'unreadMessages', (count: number) => onCount(count || 0));
    // Zera o badge quando o Messenger é aberto.
    this.zE('messenger:on', 'open', () => onCount(0));

    return true;
  }
}
